<!-- src/apps/workspace/domains/DomainVerify.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import DnsWidget from '@/apps/workspace/components/domains/DnsWidget.vue';
  import DomainHeader from '@/apps/workspace/components/dashboard/DomainHeader.vue';
  import DomainVerificationInfo from '@/apps/workspace/components/domains/DomainVerificationInfo.vue';
  import MoreInfoText from '@/shared/components/ui/MoreInfoText.vue';
  import VerifyDomainDetails from '@/apps/workspace/components/domains/VerifyDomainDetails.vue';
  import { useDomainsManager } from '@/shared/composables/useDomainsManager';
  import { useDomain } from '@/shared/composables/useDomain';
  import { useBootstrapStore } from '@/shared/stores/bootstrapStore';
  import { CustomDomainProxy, type CustomDomainResponse } from '@/schemas/api/v3/responses/domains';
  import { storeToRefs } from 'pinia';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import { computed, onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router';

  const { t } = useI18n(); // auto-import
  const router = useRouter();

  // Issue 2 fix: Use defineProps like other domain pages
  const props = defineProps<{
    extid: string;
    orgid: string;
  }>();

  // Issue 1 fix: Use useDomain composable like other pages
  const {
    domain,
    details,
    initialize: initializeDomain,
  } = useDomain(props.extid);

  // Keep useDomainsManager for verifyDomain (used by triggerVerification)
  const { verifyDomain } = useDomainsManager();

  const handleBack = () => {
    router.push(`/org/${props.orgid}/domains/${props.extid}`);
  };
  const bootstrapStore = useBootstrapStore();
  const { cust } = storeToRefs(bootstrapStore);

  // Feature flag: DNS widget UI (disabled by default for launch)
  const isDnsWidgetEnabled = computed(() => cust.value?.feature_flags?.dns_widget ?? false);

  // Cluster data comes from details (useDomain returns it)
  const cluster = computed<CustomDomainProxy | null>(() => details.value?.cluster ?? null);
  const allowVerifyCTA = ref(true);
  const verificationInProgress = ref(false);
  const lastVerificationTime = ref<number>(0);

  // Rate limit: minimum 10 seconds between verification attempts
  const VERIFICATION_COOLDOWN_MS = 10000;

  const fetchDomain = async (): Promise<void> => {
    await initializeDomain();
    // allowVerifyCTA is computed from canVerify in useDomain, but we keep a local ref
    // for compatibility with existing template bindings
  };

  const handleDomainVerify = async (data: CustomDomainResponse) => {
    console.debug('Domain verified: refreshing domain info', data);
    await fetchDomain();
  };

  // DNS Widget configuration
  // Show widget only when:
  // 1. Feature flag is enabled (dns_widget)
  // 2. Domain exists and is not yet verified
  // 3. Cluster uses approximated validation strategy
  const showDnsWidget = computed(
    () =>
      isDnsWidgetEnabled.value &&
      domain.value &&
      !domain.value.vhost?.last_monitored_unix &&
      cluster.value?.validation_strategy === 'approximated'
  );

  // Show manual DNS instructions for non-approximated strategies
  const showManualInstructions = computed(
    () =>
      domain.value &&
      !domain.value.vhost?.last_monitored_unix &&
      cluster.value?.validation_strategy !== 'approximated'
  );

  // Target address for DNS records (IP for apex domains, hostname otherwise)
  const dnsTargetAddress = computed(() => {
    // For apex domains, use the proxy IP; otherwise use proxy_host for CNAME
    if (domain.value?.is_apex) {
      return cluster.value?.proxy_ip ?? '';
    }
    return cluster.value?.proxy_host ?? '';
  });

  // Check if enough time has passed since last verification
  const canVerify = (): boolean => {
    const now = Date.now();
    return now - lastVerificationTime.value >= VERIFICATION_COOLDOWN_MS;
  };

  // Trigger backend verification (called on mount and widget success)
  // Errors are handled via global notifications by useDomainsManager.wrap()
  const triggerVerification = async () => {
    // Guard: no domain, already in progress, or rate limited
    if (!domain.value || verificationInProgress.value || !canVerify()) {
      return;
    }

    verificationInProgress.value = true;
    lastVerificationTime.value = Date.now();

    const result = await verifyDomain(domain.value.extid);
    if (result) {
      await fetchDomain();
    }

    verificationInProgress.value = false;
  };

  const handleDnsRecordsVerified = async () => {
    console.debug('DNS records verified via widget, triggering backend verification');
    await triggerVerification();
  };

  // On mount: fetch domain then verify if showing widget
  onMounted(async () => {
    try {
      await fetchDomain();
      if (showDnsWidget.value) {
        await triggerVerification();
      }
    } catch (err: unknown) {
      // Errors from fetchDomain are handled by useDomainsManager
      // This catch is a safety net for any unexpected errors
      console.error('Failed to initialize domain verification:', err);
    }
  });
</script>

<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Back button -->
    <div class="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
      <div class="mb-4">
        <button
          type="button"
          class="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          @click="handleBack">
          <OIcon
            collection="heroicons"
            name="arrow-left"
            class="size-5"
            aria-hidden="true" />
          {{ t('web.COMMON.back') }}
        </button>
      </div>
    </div>

    <!-- Header Section -->
    <div class="sticky top-0 z-30">
      <DomainHeader
        :domain="domain"
        :has-unsaved-changes="false"
        :orgid="props.orgid"
        external-path="/" />
    </div>

    <div class="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
    <h2 class="mb-6 text-xl font-bold text-gray-900 dark:text-white">
      {{ t('web.domains.verify_your_domain') }}
    </h2>

    <DomainVerificationInfo
      v-if="domain && domain.vhost?.last_monitored_unix"
      :domain="domain"
      mode="table" />
    <p
      v-else-if="domain"
      class="mb-6 text-lg text-gray-600 dark:text-gray-300">
      {{ t('web.domains.before_we_can_activate_links_for') }}
      <span class="bg-white text-brand-600 dark:bg-gray-800 dark:text-brand-400">{{
        domain.display_domain
      }}</span>
      {{ t('web.domains.youll_need_to_complete_these_steps') }}
    </p>

    <MoreInfoText
      v-if="showManualInstructions"
      text-color="text-brandcomp-800 dark:text-gray-100"
      bg-color="bg-white dark:bg-gray-800">
      <div class="prose max-w-none">
        <div class="text-base text-gray-600 dark:text-gray-300">
          <p>
            {{ t('web.domains.in_order_to_connect_your_domain_youll_need_to_ha') }}
            <span
              class="bg-white px-2 font-bold text-brand-600 dark:bg-gray-800 dark:text-brand-400">{{ domain?.display_domain }}</span>
            at
            <span
              :title="cluster?.proxy_name ?? ''"
              class="bg-white px-2 dark:bg-gray-800">{{ cluster?.proxy_host }}</span>{{ t('web.domains.if_you_already_have_a_cname_record_for_that_addr') }}
            <span
              :title="cluster?.proxy_name ?? ''"
              class="bg-white px-2 dark:bg-gray-800">{{ cluster?.proxy_host }}</span>
            {{ t('web.domains.and_remove_any_other_a_aaaa_or_cname_records_for') }}
          </p>
          <p
            v-if="domain?.is_apex"
            class="border-l-4 border-yellow-500 bg-yellow-100 p-4 text-yellow-700">
            <!-- Disclaimer for apex domains -->
            <strong>{{ t('web.COMMON.important') }}:</strong> {{ t('web.domains.please_note_that_for_apex_domains') }}
            <span
              class="bg-white px-2 font-bold text-brand-600 dark:bg-gray-800 dark:text-brand-400">{{ domain?.display_domain }}</span>{{ t('web.domains.a_cname_record_is_not_allowed_instead_youll_need') }}
          </p>
        </div>
        <div class="mt-4 text-sm">
          <a
            href="#"
            class="font-medium text-brandcomp-600 hover:text-brandcomp-500 dark:text-brandcomp-400 dark:hover:text-brandcomp-300">
            <!-- {{ t('web.domains.learn_more_dns') }} <span aria-hidden="true">&rarr;</span> -->
          </a>
        </div>
      </div>
    </MoreInfoText>

    <!-- DNS Widget for automated DNS configuration -->
    <div
      v-if="showDnsWidget"
      class="my-8 rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
      <h2 class="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
        {{ t('web.domains.configure_dns_records') }}
      </h2>
      <p class="mb-4 text-gray-600 dark:text-gray-400">
        {{ t('web.domains.dns_widget_description') }}
      </p>
      <DnsWidget
        :domain="domain!.display_domain"
        :target-address="dnsTargetAddress"
        :is-apex="domain?.is_apex"
        :txt-validation-host="domain?.txt_validation_host"
        :txt-validation-value="domain?.txt_validation_value"
        :trd="domain?.trd ?? undefined"
        @records-verified="handleDnsRecordsVerified" />

      <!-- Manual verification button and error display -->
      <div class="mt-6 flex items-center gap-4">
        <button
          type="button"
          data-testid="verify-domain-button"
          :disabled="verificationInProgress"
          :aria-busy="verificationInProgress"
          class="inline-flex items-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-brand-500 dark:hover:bg-brand-600"
          @click="triggerVerification">
          <svg
            v-if="verificationInProgress"
            class="-ml-1 mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4" />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          {{ verificationInProgress ? t('web.COMMON.processing') : t('web.domains.verify_domain') }}
        </button>
      </div>
    </div>

    <!-- Manual verification steps for non-approximated strategies -->
    <VerifyDomainDetails
      v-if="domain && !showDnsWidget"
      :domain="domain"
      :cluster="cluster"
      :with-verify-c-t-a="allowVerifyCTA"
      @domain-verify="handleDomainVerify" />
    <p
      v-if="!domain"
      class="text-gray-600 dark:text-gray-400">
      {{ t('web.domains.loading_domain_information') }}
    </p>
    </div>
  </div>
</template>
